/**
 * Управление доступом к финансовому разделу.
 * Видна только owner/superuser/admin.
 * Список всех активных юзеров с переключателем «Доступ к финансам».
 *
 * Юзер видит /finance если:
 *  - is_superuser, или
 *  - role ∈ {admin, owner, head_op} (auto, отображается «по должности»), или
 *  - can_access_finance = true (выдано вручную тут)
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchFinanceAccess, updateFinanceAccess, type FinanceAccessUser } from "@/api/finance";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import { Avatar } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";
import { useAuth } from "@/auth/AuthContext";
import { useTaxonomy } from "@/lib/useTaxonomy";

export function FinanceAccessPage() {
  const { user: currentUser } = useAuth();
  const isOwner = !!currentUser?.is_superuser;
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const taxQ = useTaxonomy();
  const roleLabels: Record<string, string> = {};
  for (const r of (taxQ.data?.roles ?? [])) roleLabels[r.key] = r.label;
  const deptLabels: Record<string, string> = {};
  for (const d of (taxQ.data?.departments ?? [])) deptLabels[d.key] = d.label;

  const q = useQuery({
    queryKey: ["finance-admin-access"],
    queryFn: fetchFinanceAccess,
    staleTime: 30_000,
    enabled: isOwner,
  });

  const mut = useMutation({
    mutationFn: (args: { userId: string; can: boolean }) => updateFinanceAccess(args.userId, args.can),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance-admin-access"] }),
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });

  const items = q.data?.items ?? [];
  const filtered = useMemo(() => {
    if (!filter) return items;
    const f = filter.toLowerCase();
    return items.filter(u =>
      (u.display_name || "").toLowerCase().includes(f) ||
      u.email.toLowerCase().includes(f) ||
      (u.position || "").toLowerCase().includes(f),
    );
  }, [items, filter]);

  // Split: те у кого доступ vs нет
  const withAccess = filtered.filter(u => u.has_role_access || u.can_access_finance);
  const withoutAccess = filtered.filter(u => !u.has_role_access && !u.can_access_finance);

  if (!isOwner) {
    return (
      <FinanceLayout>
                <div className="card muted" style={{ padding: 32, textAlign: "center" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="lock" size={14}/> Управление доступом — только собственникам</span>
        </div>
      </FinanceLayout>
    );
  }

  return (
    <div className="page">
      
      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <span style={{ color: "var(--mute)" }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" ><path d="M4 7h8M16 7h4M4 17h4M12 17h8M14 4v6M8 14v6"/></svg></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Доступ к финансовому разделу</div>
            <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 2 }}>
              Кто видит /finance: пресет «по должности» (Собственник, Админ, Руководитель ОП) + индивидуально выданный доступ.
            </div>
          </div>
        </div>
        <input className="input" type="search" placeholder="Поиск по имени / email / должности…"
               value={filter} onChange={(e) => setFilter(e.target.value)}
               style={{ width: "100%", maxWidth: 400 }}/>
      </div>

      {q.isLoading && <div className="muted" style={{ padding: 20 }}>Загрузка…</div>}
      {q.error && <div className="alert alert-red"><div className="alert-body">{(q.error as Error).message}</div></div>}

      {!q.isLoading && !q.error && (
        <>
          {/* С доступом */}
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
            С доступом ({withAccess.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
            {withAccess.length === 0 && <div className="muted" style={{ padding: 12, fontSize: 13 }}>Никто не имеет доступ</div>}
            {withAccess.map(u => (
              <AccessRow key={u.user_id} user={u}
                         roleLabels={roleLabels} deptLabels={deptLabels}
                         disabled={mut.isPending}
                         onToggle={(can) => mut.mutate({ userId: u.user_id, can })}/>
            ))}
          </div>

          {/* Без доступа */}
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
            Без доступа ({withoutAccess.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {withoutAccess.length === 0 && <div className="muted" style={{ padding: 12, fontSize: 13 }}>Все имеют доступ</div>}
            {withoutAccess.map(u => (
              <AccessRow key={u.user_id} user={u}
                         roleLabels={roleLabels} deptLabels={deptLabels}
                         disabled={mut.isPending}
                         onToggle={(can) => mut.mutate({ userId: u.user_id, can })}/>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function AccessRow({ user, roleLabels, deptLabels, disabled, onToggle }: {
  user: FinanceAccessUser;
  roleLabels: Record<string, string>;
  deptLabels: Record<string, string>;
  disabled: boolean;
  onToggle: (can: boolean) => void;
}) {
  const positionLabel = user.position
    || (user.is_superuser ? "Собственник" : (roleLabels[user.role] || user.role));
  const depts = user.departments.map(d => deptLabels[d] || d).join(", ");
  const hasAccess = user.has_role_access || user.can_access_finance;

  return (
    <div className="card" style={{
      padding: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      borderLeft: hasAccess ? "3px solid var(--green)" : "3px solid var(--line)",
    }}>
      <Avatar name={user.display_name || user.email} userId={user.user_id}
              avatarFilename={user.avatar_filename} size={36} color="#4fae4e"/>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)" }}>
          {user.display_name || user.email}
        </div>
        <div style={{ fontSize: 11, color: "var(--mute)" }}>
          {positionLabel}{depts && ` · ${depts}`}
        </div>
      </div>

      {user.has_role_access ? (
        <span style={{
          background: "rgba(61,122,61,0.15)", color: "var(--green)",
          padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
        }}
        title="Доступ выдан автоматически по должности (Собственник / Админ / Руководитель ОП)">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="check" size={11}/>По должности</span>
        </span>
      ) : (
        <button type="button"
                onClick={() => onToggle(!user.can_access_finance)}
                disabled={disabled}
                className={user.can_access_finance ? "btn btn-sm btn-ghost" : "btn btn-sm btn-primary"}
                style={{ flexShrink: 0 }}>
          {user.can_access_finance ? "Отключить" : "Дать доступ"}
        </button>
      )}
    </div>
  );
}
